import { WarningCircle, TrendUp, TrendDown, Minus, CornersOut, Barn } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STYLE = {
  green: { bg: "#ECF7EF", color: "#2F6B4F", border: "rgba(91,138,111,0.22)" },
  yellow: { bg: "#FFF6DE", color: "#8A6A3B", border: "rgba(232,168,56,0.28)" },
  red: { bg: "#FDECE8", color: "#B23B1F", border: "rgba(217,108,74,0.28)" },
};

function TrendIcon({ trend }) {
  if (trend === "↑") return <TrendUp size={14} weight="bold" />;
  if (trend === "↓") return <TrendDown size={14} weight="bold" />;
  return <Minus size={14} weight="bold" />;
}

export function AgricultureMarginWatchlistCard({ data, onOpenCategory }) {
  if (!data) return null;
  const ratioTone = data.input_ratio_severity === "critical" ? STYLE.red : data.input_ratio_severity === "warning" ? STYLE.yellow : STYLE.green;
  return (
    <section className="rounded-[28px] p-6 sm:p-7 mb-10 overflow-hidden relative" style={{ background: "linear-gradient(135deg, rgba(251,248,238,0.98) 0%, rgba(244,239,228,0.96) 100%)", border: "1px solid rgba(232,168,56,0.22)" }} data-testid="ag-margin-watchlist-card">
      <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full" style={{ background: "rgba(232,168,56,0.10)" }} />
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "rgba(255,255,255,0.7)", color: "var(--bk-accent)" }}>
            <Barn size={14} weight="duotone" /> Margin Watchlist
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-bold mt-3" style={{ color: "var(--bk-text)" }}>Agriculture margin watchlist</h3>
          <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--bk-text-2)" }} data-testid="ag-margin-rowan-primary">{data.rowan_primary}</p>
        </div>
        <div className="rounded-2xl px-4 py-3" style={{ background: ratioTone.bg, color: ratioTone.color, border: `1px solid ${ratioTone.border}` }} data-testid="ag-margin-ratio-pill">
          <p className="text-xs uppercase tracking-[0.16em] font-semibold">Input costs / revenue</p>
          <p className="font-display text-3xl font-bold mt-1">{data.input_cost_ratio_pct}%</p>
          <p className="text-xs mt-1">{money(data.current_input_total)} of {money(data.current_revenue || 0)} this month</p>
        </div>
      </div>

      <div className="relative grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-5 mt-6">
        <div className="bk-card p-5" data-testid="ag-margin-watchlist-items">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Monitored inputs</p>
            <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>{data.current_period_label} vs {data.prior_period_label}</p>
          </div>
          <div className="space-y-3">
            {(data.items || []).map((item) => {
              const tone = STYLE[item.color] || STYLE.green;
              return (
                <button
                  key={item.key}
                  onClick={() => onOpenCategory?.(item.primary_category)}
                  data-testid={`ag-margin-item-${item.key}`}
                  className="w-full text-left rounded-2xl border p-4 transition-transform hover:-translate-y-0.5"
                  style={{ borderColor: tone.border, background: "#fff" }}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{item.label}</p>
                      <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{money(item.current_30_total)} now · {money(item.prior_30_total)} prior</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: tone.bg, color: tone.color }} data-testid={`ag-margin-severity-${item.key}`}>
                        <TrendIcon trend={item.trend} /> {item.pct_change > 0 ? `+${item.pct_change}%` : `${item.pct_change}%`}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--bk-text-2)" }}>
                        Drill in <CornersOut size={13} weight="bold" />
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bk-card p-5" data-testid="ag-margin-alert-summary">
          <div className="flex items-center gap-2 mb-3">
            <WarningCircle size={18} weight="duotone" color={ratioTone.color} />
            <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Watchlist summary</p>
          </div>
          <p className="font-display text-3xl font-bold" style={{ color: "var(--bk-text)" }}>{data.flagged_count}</p>
          <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>categories up 10%+ in the last 30 days</p>
          <div className="mt-4 rounded-2xl p-4" style={{ background: ratioTone.bg, color: ratioTone.color }}>
            <p className="text-xs uppercase tracking-[0.16em] font-semibold">Margin status</p>
            <p className="text-sm font-semibold mt-2">{data.input_ratio_severity === "critical" ? "Critical — input costs are crowding revenue." : data.input_ratio_severity === "warning" ? "Warning — margins are getting tight." : "Stable — costs look manageable right now."}</p>
          </div>
        </div>
      </div>
    </section>
  );
}